import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { TaskPriorityOptions } from 'shared/types/task-base';

import { usePriorityTree } from '../../hooks/use-priority-tree';
import { GroupedListProps } from '../../types';
import { PriorityHeader } from '../priority-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './priority-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const PriorityGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = usePriorityTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }
        const taskToUpdate = structuredClone(tasksMap[item.id]);
        const priorityItem = updatedItems.find(({ children }) =>
          children?.includes(item.id),
        );

        taskToUpdate.priority =
          (priorityItem?.id as TaskPriorityOptions) ?? TaskPriorityOptions.None;

        updatedDocs.push(taskToUpdate);
      }
      if (item.type === TreeItemTypes.Priority) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const priorityId = item.sourceId.replace('-new-task', '');
          const priority =
            priorityId === TaskPriorityOptions.None
              ? undefined
              : (priorityId as TaskPriorityOptions);
          return (
            <NewTaskRow
              priority={priority}
              date={defaultNewTaskDate}
              startTime={null}
              duration={null}
              goal={groupSettings.goalId}
              lifeArea={groupSettings.lifeAreaId}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // Priority
        if (type === TreeItemTypes.Priority) {
          return (
            <PriorityHeader denseFormat={denseFormat} item={itemInstance} />
          );
        }
      },
    [
      denseFormat,
      defaultNewTaskDate,
      groupSettings.goalId,
      groupSettings.lifeAreaId,
      groupSettings.tagId,
      groupSettings.displayDue,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
